import { motion } from "framer-motion";
import { Building2, Plane, Route, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "+966540423401";

const COPY = {
  en: {
    eyebrow: "Core Services",
    title: "The private travel services BlackRide is built to deliver well.",
    cta: "Discuss this service on WhatsApp",
    services: [
      {
        icon: Plane,
        title: "Airport chauffeur transfers",
        desc: "Premium airport arrivals for Jeddah, hotel pickups, hosted guests, and travelers who want a more dependable first impression after landing.",
      },
      {
        icon: Route,
        title: "Jeddah, Makkah, and Madinah intercity travel",
        desc: "Private road movement for pilgrims, families, executives, and premium guests who need more than a one-city ride.",
      },
      {
        icon: Building2,
        title: "Corporate and executive chauffeur service",
        desc: "Structured around meetings, hosted guests, airport arrivals, and business schedules where timing and presentation matter.",
      },
      {
        icon: Sparkles,
        title: "VIP, family, and Umrah travel",
        desc: "A higher-touch service standard for family arrivals, elders, premium hospitality, and pilgrimage journeys that need more care.",
      },
    ],
  },
  ar: {
    eyebrow: "الخدمات الأساسية",
    title: "الخدمات الخاصة التي صُممت بلاك رايد لتقديمها بمستوى متميز.",
    cta: "ناقش هذه الخدمة عبر واتساب",
    services: [
      {
        icon: Plane,
        title: "تنقلات المطار مع سائق خاص",
        desc: "وصولات راقية من مطار جدة، واستقبال الفنادق، والضيوف المستضافين، والمسافرين الباحثين عن انطباع أول أكثر موثوقية بعد الهبوط.",
      },
      {
        icon: Route,
        title: "التنقل بين جدة ومكة والمدينة",
        desc: "تنقل خاص للحجاج والمعتمرين والعائلات والتنفيذيين والضيوف المميزين الذين يحتاجون إلى أكثر من رحلة داخل مدينة واحدة.",
      },
      {
        icon: Building2,
        title: "خدمة سائق خاص للأعمال والتنفيذيين",
        desc: "مبنية حول الاجتماعات والضيوف المستضافين والوصولات من المطار والجداول العملية التي يهم فيها الوقت والانطباع.",
      },
      {
        icon: Sparkles,
        title: "سفر VIP والعائلات والعمرة",
        desc: "مستوى خدمة أكثر عناية لوصولات العائلات وكبار السن والضيافة الراقية ورحلات العمرة التي تحتاج إلى اهتمام أكبر.",
      },
    ],
  },
};

export default function Services() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;

  return (
    <section id="services" data-testid="services-section" className="bg-[#050505] py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="mb-14"
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-[#C7A239]">{copy.eyebrow}</p>
          <h2 className="max-w-4xl font-['Playfair_Display'] text-3xl font-semibold leading-tight text-white md:text-4xl lg:text-5xl">
            {copy.title}
          </h2>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2">
          {copy.services.map((service, index) => {
            const message = encodeURIComponent(
              `Hello BlackRide! I am interested in your ${service.title} service. Please share availability and guidance.`,
            );
            const href = `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, "")}?text=${message}`;

            const Icon = service.icon;

            return (
              <motion.article
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="rounded-[30px] border border-white/8 bg-[#111] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#C7A239]/22 hover:bg-[#151515]"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C7A239]/10">
                  <Icon className="h-5 w-5 text-[#C7A239]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-2xl text-white">{service.title}</h3>
                <p className="mt-4 text-sm leading-7 text-[#a79f93]">{service.desc}</p>
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 font-medium text-[#C7A239] transition-colors hover:text-[#D4B34A]"
                >
                  {copy.cta}
                  <span aria-hidden="true">&rarr;</span>
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
